<template>
  <b-modal
    :model-value="visible"
    title="import rows"
    size="lg"
    @update:model-value="$emit('update:visible', $event)"
    @hide="onHide"
  >
    <template #title>
      import rows
      <modal-help topic="import-rows" />
    </template>
    <b-container fluid>
      <b-form v-if="!parsed">
        <b-form-group label="CSV file">
          <b-form-file
            v-model="file"
            accept=".csv,text/csv"
            placeholder="Choose a CSV file..."
            @change="onFile"
          />
        </b-form-group>
        <p class="text-muted small">The first row is the column titles. Nothing is written until you import.</p>
      </b-form>
      <div v-else>
        <p class="text-muted small">{{ rows.length }} row{{ rows.length === 1 ? '' : 's' }} in this file. Map each file column onto a column of {{ sheetTitle }}.</p>
        <div class="import-column-list">
          <div v-for="(header, index) in headers" :key="'map-' + index" class="import-map-row">
            <span class="import-column-name">{{ header || ('Column ' + (index + 1)) }}</span>
            <b-form-select
              :model-value="mapping[index]"
              :options="optionsFor(index)"
              @update:model-value="onMap(index, $event)"
            />
          </div>
        </div>
        <b-form-group label="Match existing rows by" class="mt-3">
          <b-form-select v-model="keyColumn" :options="keyOptions" @update:model-value="clearPreview" />
          <small class="text-muted">When this column matches one record, that record is updated. Every other row is inserted.</small>
        </b-form-group>
        <b-form-group v-if="needsTimeZone" label="file time zone">
          <b-form-select v-model="timeZone" :options="timeZoneOptions" @update:model-value="clearPreview" />
          <small class="text-muted">Date and time columns use this zone. The import stops when it is missing.</small>
        </b-form-group>
        <b-form-checkbox v-model="acceptInvalid" class="mb-2" @update:model-value="clearPreview">Load rows that fail a validation rule</b-form-checkbox>
        <div v-if="preview" class="import-preview">
          <p>{{ preview.counts.insert }} row{{ preview.counts.insert === 1 ? '' : 's' }} will be inserted.</p>
          <p>{{ preview.counts.update }} row{{ preview.counts.update === 1 ? '' : 's' }} will be updated.</p>
          <p>{{ preview.counts.failed }} row{{ preview.counts.failed === 1 ? '' : 's' }} failed and will not be saved.</p>
          <p v-if="loadedFailureCount">{{ loadedFailureCount }} row{{ loadedFailureCount === 1 ? '' : 's' }} failed a validation rule and will be loaded.</p>
          <div v-if="preview.failures.length" class="import-failure-list">
            <div v-for="row in preview.failures" :key="'fail-' + row.index">Row {{ row.index + 1 }}: {{ row.message }}</div>
          </div>
          <b-button v-if="preview.failures.length" size="sm" variant="outline-secondary" class="mt-2" @click="downloadReport">Download failed rows</b-button>
        </div>
        <b-alert v-if="error" variant="danger" show class="mt-3">{{ error }}</b-alert>
        <b-button size="sm" variant="outline-secondary" class="mt-2" @click="reset">Choose a different file</b-button>
      </div>
    </b-container>
    <template #footer>
      <b-button variant="secondary" @click="$emit('update:visible', false)">Cancel</b-button>
      <b-button v-if="parsed" variant="outline-primary" :disabled="busy || !canPreview" @click="runPreview">
        {{ busy && !committing ? 'Previewing…' : 'Preview' }}
      </b-button>
      <b-button variant="primary" :disabled="busy || !preview || !canCommit" @click="runCommit">
        {{ committing ? 'Importing…' : 'Import' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { csvFilename } from '../../../shared/spreadsheet-csv.js'
import {
  parseCsv,
  suggestColumnMap,
  importableColumns,
  keyColumnAllowed
} from '../../../shared/spreadsheet-import.js'
import { isDateOnlyColumn, isValidTimeZone, timeZoneChoices, timeZoneLabel } from '../../../shared/app-timezone.js'

export default {
  props: {
    visible: { type: Boolean, default: false },
    spreadSheet: { type: Object, default: null },
    applicationUniqueTitle: { type: String, default: '' }
  },
  emits: ['update:visible', 'imported'],
  data () {
    return {
      file: null,
      parsed: false,
      headers: [],
      rows: [],
      mapping: [],
      keyColumn: '',
      timeZone: '',
      acceptInvalid: false,
      preview: null,
      error: null,
      busy: false,
      committing: false
    }
  },
  computed: {
    sheetTitle () {
      return (this.spreadSheet && (this.spreadSheet.title || this.spreadSheet.uniqueTitle)) || 'this spreadsheet'
    },
    sheetColumns () {
      return importableColumns(this.spreadSheet && this.spreadSheet.columns)
    },
    needsTimeZone () {
      return this.mapping.some((uniqueTitle) => {
        const column = this.columnById(uniqueTitle)
        return column && column.type === 'dateTime' && !isDateOnlyColumn(column)
      })
    },
    timeZoneOptions () {
      return [{ value: '', text: 'Choose the file time zone' }].concat(
        timeZoneChoices().map((zone) => ({ value: zone, text: timeZoneLabel(zone) }))
      )
    },
    keyOptions () {
      const mapped = this.mapping
        .map((uniqueTitle) => this.columnById(uniqueTitle))
        .filter((column) => keyColumnAllowed(column))
      const options = [{ value: '', text: 'Do not match — insert every row' }]
      const seen = new Set()
      for (const column of mapped) {
        if (seen.has(column.uniqueTitle)) continue
        seen.add(column.uniqueTitle)
        options.push({ value: column.uniqueTitle, text: column.title || column.uniqueTitle })
      }
      return options
    },
    canPreview () {
      if (!this.parsed || !this.rows.length) return false
      if (!this.mapping.some((uniqueTitle) => uniqueTitle)) return false
      if (this.needsTimeZone && !isValidTimeZone(this.timeZone)) return false
      return true
    },
    canCommit () {
      return !!(this.preview && (this.preview.counts.insert || this.preview.counts.update))
    },
    loadedFailureCount () {
      if (!this.preview || !Array.isArray(this.preview.failures)) return 0
      return this.preview.failures.filter((row) => row.loaded).length
    }
  },
  watch: {
    visible (open) {
      if (open) this.reset()
    }
  },
  methods: {
    columnById (uniqueTitle) {
      if (!uniqueTitle) return null
      return this.sheetColumns.find((column) => column.uniqueTitle === uniqueTitle) || null
    },
    optionsFor (index) {
      const current = this.mapping[index]
      const choices = this.sheetColumns.filter((column) => {
        return column.uniqueTitle === current || !this.mapping.includes(column.uniqueTitle)
      }).map((column) => ({
        value: column.uniqueTitle,
        text: `${column.title || column.uniqueTitle} (${column.type})`
      }))
      return [{ value: '', text: 'Skip' }].concat(choices)
    },
    onFile (event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (loadEvent) => {
        const text = loadEvent.target && loadEvent.target.result
        if (!text || typeof text !== 'string') return
        const parsed = parseCsv(text)
        if (!parsed.headers.length || !parsed.rows.length) {
          this.error = 'The file needs a header row and at least one record.'
          return
        }
        this.headers = parsed.headers
        this.rows = parsed.rows
        this.mapping = suggestColumnMap(parsed.headers, this.sheetColumns)
        this.keyColumn = ''
        this.parsed = true
        this.error = null
        this.preview = null
      }
      reader.readAsText(file, 'UTF-8')
    },
    onMap (index, uniqueTitle) {
      const next = this.mapping.slice()
      next[index] = uniqueTitle || ''
      this.mapping = next
      if (this.keyColumn && !next.includes(this.keyColumn)) this.keyColumn = ''
      this.clearPreview()
    },
    clearPreview () {
      this.preview = null
    },
    requestBody () {
      return {
        headers: this.headers,
        rows: this.rows,
        mapping: this.mapping,
        keyColumn: this.keyColumn,
        timeZone: this.timeZone,
        acceptInvalid: this.acceptInvalid
      }
    },
    async runPreview () {
      if (!this.canPreview || !this.spreadSheet) return
      this.busy = true
      this.committing = false
      this.error = null
      try {
        const response = await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.spreadSheet.uniqueTitle}/import/preview`,
          this.requestBody(),
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.preview = response.data
      } catch (error) {
        const message = error && error.response && error.response.data
        this.error = typeof message === 'string' && message.trim() ? message : 'Could not preview this file.'
      }
      this.busy = false
    },
    async runCommit () {
      if (!this.canCommit || !this.spreadSheet) return
      this.busy = true
      this.committing = true
      this.error = null
      try {
        const response = await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.spreadSheet.uniqueTitle}/import`,
          this.requestBody(),
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.preview = response.data
        this.$emit('imported')
        if (!response.data.counts.failed) {
          this.$emit('update:visible', false)
          this.reset()
        } else {
          this.error = `${response.data.counts.failed} row${response.data.counts.failed === 1 ? '' : 's'} failed and were not saved.`
        }
      } catch (error) {
        const message = error && error.response && error.response.data
        this.error = typeof message === 'string' && message.trim() ? message : 'Could not import this file.'
      }
      this.busy = false
      this.committing = false
    },
    downloadReport () {
      const csv = this.preview && this.preview.report
      if (!csv) return
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = csvFilename(`${this.sheetTitle}-import-failures`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
    reset () {
      this.file = null
      this.parsed = false
      this.headers = []
      this.rows = []
      this.mapping = []
      this.keyColumn = ''
      this.timeZone = ''
      this.acceptInvalid = false
      this.preview = null
      this.error = null
      this.busy = false
      this.committing = false
    },
    onHide () {
      this.reset()
    }
  }
}
</script>

<style>
.import-column-list {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  padding: 8px 12px;
  background-color: #141412;
}
.import-column-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #e8e8e6;
}
.import-map-row {
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) minmax(12rem, 1.4fr);
  gap: 12px;
  align-items: center;
  padding: 6px 0;
}
.import-preview {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  background: #141412;
}
.import-preview p {
  margin: 0 0 4px;
}
.import-failure-list {
  max-height: 160px;
  overflow-y: auto;
  margin-top: 8px;
  color: #e0c36a;
  font-size: 0.9rem;
}
</style>
